import { Component, OnInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PericiasService } from '../../core/services/pericias.service';
import { Pericia } from '../../core/models/pericia.model';

interface StatusResumo {
  status: string;
  total: number;
  percentual: number;
  classe: string;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements OnInit {
  pericias = signal<Pericia[]>([]);
  carregando = signal(false);
  erro = signal('');

  readonly statusOrdem = [
    'Planejamento',
    'Em andamento',
    'Em análise',
    'Concluída',
    'Suspensa',
    'Cancelada'
  ];

  readonly statusClasses: Record<string, string> = {
    'Planejamento': 'status-planejamento',
    'Em andamento': 'status-andamento',
    'Em análise': 'status-analise',
    'Concluída': 'status-concluida',
    'Suspensa': 'status-suspensa',
    'Cancelada': 'status-cancelada'
  };

  constructor(private readonly periciasService: PericiasService) {}

  ngOnInit(): void {
    this.carregarPericias();
  }

  carregarPericias(): void {
    this.carregando.set(true);
    this.erro.set('');

    this.periciasService.listar().subscribe({
      next: (dados) => {
        this.pericias.set(dados ?? []);
        this.carregando.set(false);
      },
      error: () => {
        this.erro.set('Não foi possível carregar os dados das perícias para o dashboard.');
        this.carregando.set(false);
      }
    });
  }

  totalPericias = computed(() => this.pericias().length);

  resumoStatus = computed<StatusResumo[]>(() => {
    const total = this.totalPericias();
    const mapa = new Map<string, number>();

    for (const pericia of this.pericias()) {
      const status = pericia.status || 'Sem status';
      mapa.set(status, (mapa.get(status) ?? 0) + 1);
    }

    const listaOrdenada = [
      ...this.statusOrdem.filter(status => mapa.has(status)),
      ...Array.from(mapa.keys()).filter(status => !this.statusOrdem.includes(status)).sort()
    ];

    return listaOrdenada.map(status => {
      const qtd = mapa.get(status) ?? 0;
      return {
        status,
        total: qtd,
        percentual: total ? Math.round((qtd / total) * 100) : 0,
        classe: this.statusClasses[status] ?? 'status-outros'
      };
    });
  });

  graficoPizza = computed(() => {
    const resumo = this.resumoStatus();
    const total = this.totalPericias();

    if (!total || !resumo.length) {
      return '#eef3ff 0deg 360deg';
    }

    let inicio = 0;
    const partes = resumo.map(item => {
      const graus = (item.total / total) * 360;
      const fim = inicio + graus;
      const cor = this.corStatus(item.status);
      const fatia = `${cor} ${inicio}deg ${fim}deg`;
      inicio = fim;
      return fatia;
    });

    return partes.join(', ');
  });

  corStatus(status: string): string {
    switch (status) {
      case 'Planejamento': return '#7c3aed';
      case 'Em andamento': return '#2563eb';
      case 'Em análise': return '#f59e0b';
      case 'Concluída': return '#16a34a';
      case 'Suspensa': return '#64748b';
      case 'Cancelada': return '#dc2626';
      default: return '#0891b2';
    }
  }
}
